import { Link } from 'react-router-dom';
import Layout from '@/components/feature/Layout';
import VideoCard from '@/components/base/VideoCard';
import SectionHeading from '@/components/base/SectionHeading';
import HeroCarousel from './components/HeroCarousel';
import CategoryStrip from './components/CategoryStrip';
import { useSeo } from '@/hooks/useSeo';
import { videos, siteIntro } from '@/mocks/videos';

const FEATURES = [
  { icon: 'ri-hd-line', title: '高清画质', desc: '支持高清画质浏览与展示，画面细节清晰呈现。' },
  { icon: 'ri-layout-grid-line', title: '分类完善', desc: '电影、剧集、短片、综艺、动漫与纪录片一站集齐。' },
  { icon: 'ri-smartphone-line', title: '多端适配', desc: '手机与电脑都能顺畅浏览，随时随地想看就看。' },
  { icon: 'ri-rocket-2-line', title: '每日更新', desc: '持续为你发现最新、最热的精彩视频内容。' },
];

export default function Home() {
  useSeo({
    title: '啪啪视频-高清视频资源分享与在线播放平台',
    description:
      '啪啪视频为用户提供丰富的高清视频内容，涵盖多种类型的视频资源，方便用户随时在线观看和探索最新热门影片。',
    keywords: '啪啪视频,高清视频,视频资源,影视视频,在线视频播放,视频分享,最新视频',
  });

  const featured = videos.filter((v) => v.featured);
  const latest = [...videos].sort((a, b) => b.uploadDate.localeCompare(a.uploadDate)).slice(0, 8);
  const popular = [...videos].sort((a, b) => b.views - a.views).slice(0, 8);

  return (
    <Layout transparentNav>
      <HeroCarousel videos={featured} />

      <section className="px-4 md:px-6 py-10 md:py-14">
        <SectionHeading
          title="热门分类"
          subtitle="按类型快速找到想看的内容"
          icon="ri-apps-2-line"
        />
        <CategoryStrip />
      </section>

      <section className="px-4 md:px-6 py-4 md:py-6">
        <SectionHeading
          title="最新视频"
          subtitle="按更新时间为您推荐刚刚上线的作品"
          icon="ri-time-line"
          action={{ label: '全部视频', to: '/search' }}
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {latest.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14">
        <SectionHeading
          title="热门视频"
          subtitle="根据播放量与评分精选的高人气内容"
          icon="ri-fire-line"
          action={{ label: '更多热门', to: '/search' }}
        />
        <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {popular.map((video) => (
            <VideoCard key={video.id} video={video} />
          ))}
        </div>
      </section>

      <section className="px-4 md:px-6 py-10 md:py-14 bg-background-100/60 border-y border-background-200/70">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4 md:gap-5">
          {FEATURES.map((f) => (
            <div key={f.title} className="rounded-lg border border-background-200/70 bg-background-50 p-5">
              <span className="w-11 h-11 flex items-center justify-center rounded-md bg-accent-500/15 text-accent-500">
                <i className={`${f.icon} text-2xl`} />
              </span>
              <h3 className="mt-4 font-heading text-base font-bold text-foreground-950">{f.title}</h3>
              <p className="mt-2 text-sm text-foreground-600 leading-relaxed">{f.desc}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="px-4 md:px-6 py-12 md:py-20">
        <div className="grid grid-cols-1 lg:grid-cols-2 gap-8 lg:gap-12 items-center">
          <div>
            <span className="inline-flex items-center gap-2 px-3 py-1 rounded-md bg-primary-500/15 text-primary-500 text-xs font-semibold">
              <i className="ri-information-line" />
              关于啪啪视频
            </span>
            <h2 className="mt-4 font-heading text-2xl md:text-3xl font-extrabold text-foreground-950 leading-tight">
              {siteIntro.slogan}
            </h2>
            <p className="mt-4 text-sm md:text-base text-foreground-600 leading-relaxed">{siteIntro.intro}</p>
            <div className="mt-6 flex flex-wrap gap-3">
              <Link
                to="/about"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md bg-primary-500 text-foreground-950 text-sm font-semibold hover:bg-primary-600 transition-colors whitespace-nowrap"
              >
                了解更多
                <i className="ri-arrow-right-line text-base" />
              </Link>
              <Link
                to="/search"
                className="inline-flex items-center gap-2 h-11 px-5 rounded-md border border-background-200/70 text-foreground-800 text-sm font-medium hover:border-accent-500 hover:text-accent-500 transition-colors whitespace-nowrap"
              >
                <i className="ri-search-line text-base" />
                去发现视频
              </Link>
            </div>
          </div>
          <div className="relative w-full h-[260px] md:h-[360px] rounded-lg overflow-hidden border border-background-200/70">
            <img
              src="https://readdy.ai/api/search-image?query=Stylized%20dark%20streaming%20platform%20concept%20art%2C%20a%20cozy%20home%20living%20room%20at%20night%20with%20a%20large%20glowing%20screen%20showing%20abstract%20colorful%20video%20frames%2C%20warm%20orange%20ambient%20light%20against%20deep%20indigo%20darkness%2C%20cinematic%20atmosphere%2C%20volumetric%20haze%2C%20ultra%20detailed%2C%20no%20text&width=1200&height=800&seq=pv-about-01&orientation=landscape"
              alt="啪啪视频 在线观看体验"
              title="啪啪视频高清在线观看体验"
              className="w-full h-full object-cover object-top"
            />
          </div>
        </div>
      </section>
    </Layout>
  );
}